/**
 * Render padded prep output to a PNG blob (padding, fill color, round corners).
 */

import {
  computePaddedCanvasLayout,
  normalizePaddingSettings,
  resolveCornerRadiusPx,
} from "../../../utils/canvasPaddingLayout";

function loadImage(url) {
  return new Promise((resolve, reject) => {
    if (!url) {
      reject(new Error("Missing image URL"));
      return;
    }
    const img = new Image();
    if (
      typeof url === "string" &&
      /^https?:\/\//i.test(url) &&
      !url.startsWith("blob:") &&
      !url.startsWith("data:")
    ) {
      img.crossOrigin = "anonymous";
    }
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Failed to load image for export"));
    img.src = url;
  });
}

function isTransparentFill(color) {
  if (color == null || color === "") return true;
  const raw = String(color).trim().toLowerCase();
  if (!raw || raw === "transparent" || raw === "none") return true;
  if (raw === "rgba(0,0,0,0)" || raw === "rgb(0,0,0,0)") return true;
  const alphaMatch = raw.match(/^rgba?\([^,]+,[^,]+,[^,]+,?\s*([0-9.]+)\s*\)$/);
  if (alphaMatch && Number(alphaMatch[1]) === 0) return true;
  return false;
}

function clampCornerRadii(width, height, radii, scale = 1) {
  const maxR = Math.min(width, height) / 2;
  return {
    tl: Math.max(0, Math.min((radii?.tl || 0) * scale, maxR)),
    tr: Math.max(0, Math.min((radii?.tr || 0) * scale, maxR)),
    br: Math.max(0, Math.min((radii?.br || 0) * scale, maxR)),
    bl: Math.max(0, Math.min((radii?.bl || 0) * scale, maxR)),
  };
}

function traceRoundRect(ctx, x, y, width, height, radius) {
  const r = Math.max(0, Math.min(Number(radius) || 0, width / 2, height / 2));
  ctx.beginPath();
  if (!r) {
    ctx.rect(x, y, width, height);
    return;
  }
  if (typeof ctx.roundRect === "function") {
    ctx.roundRect(x, y, width, height, r);
    return;
  }
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + width, y, x + width, y + height, r);
  ctx.arcTo(x + width, y + height, x, y + height, r);
  ctx.arcTo(x, y + height, x, y, r);
  ctx.arcTo(x, y, x + width, y, r);
  ctx.closePath();
}

function traceRoundRectRadii(ctx, x, y, width, height, radii) {
  const { tl, tr, br, bl } = clampCornerRadii(width, height, radii, 1);
  ctx.beginPath();
  if (!tl && !tr && !br && !bl) {
    ctx.rect(x, y, width, height);
    return;
  }
  if (typeof ctx.roundRect === "function") {
    ctx.roundRect(x, y, width, height, [tl, tr, br, bl]);
    return;
  }
  const maxR = Math.max(tl, tr, br, bl);
  // Fallback: uniform radius when roundRect is unavailable.
  ctx.moveTo(x + maxR, y);
  ctx.arcTo(x + width, y, x + width, y + height, maxR);
  ctx.arcTo(x + width, y + height, x, y + height, maxR);
  ctx.arcTo(x, y + height, x, y, maxR);
  ctx.arcTo(x, y, x + width, y, maxR);
  ctx.closePath();
}

/** Punch canvas alpha to a rounded rectangle (leaves RGB untouched where alpha remains). */
function applyAlphaMask(ctx, width, height, buildPath) {
  ctx.save();
  ctx.globalCompositeOperation = "destination-in";
  ctx.fillStyle = "#ffffff";
  buildPath();
  ctx.fill();
  ctx.restore();
}

/**
 * Draw the subject with rounded corners onto a transparent buffer, then blit.
 * Avoids clip()+drawImage edge cases that can leave opaque corner pixels.
 */
function drawRoundedImage(ctx, img, x, y, w, h, radii) {
  const dw = Math.max(1, Math.round(w));
  const dh = Math.max(1, Math.round(h));
  const { tl, tr, br, bl } = clampCornerRadii(dw, dh, radii, 1);
  const needsMask = tl > 0 || tr > 0 || br > 0 || bl > 0;

  if (!needsMask) {
    ctx.drawImage(img, x, y, dw, dh);
    return;
  }

  const tmp = document.createElement("canvas");
  tmp.width = dw;
  tmp.height = dh;
  const tctx = tmp.getContext("2d");
  if (!tctx) {
    ctx.drawImage(img, x, y, dw, dh);
    return;
  }

  tctx.clearRect(0, 0, dw, dh);
  tctx.drawImage(img, 0, 0, dw, dh);
  applyAlphaMask(tctx, dw, dh, () => {
    traceRoundRectRadii(tctx, 0, 0, dw, dh, { tl, tr, br, bl });
  });
  ctx.drawImage(tmp, x, y);
}

/**
 * @param {string} imageUrl
 * @param {object} paddingSettings
 * @param {{ maxEdge?: number }} [options]
 * @returns {Promise<Blob>}
 */
export async function exportPaddedImageToPngBlob(
  imageUrl,
  paddingSettings,
  { maxEdge = 2000 } = {}
) {
  const img = await loadImage(imageUrl);
  const iw = img.naturalWidth || img.width;
  const ih = img.naturalHeight || img.height;
  if (!iw || !ih) {
    throw new Error("Invalid image dimensions");
  }

  const settings = normalizePaddingSettings(paddingSettings);
  const layout = computePaddedCanvasLayout(iw, ih, iw, ih, settings);
  const cw = layout.canvasWidth || iw;
  const ch = layout.canvasHeight || ih;
  const scale = Math.min(1, maxEdge / Math.max(cw, ch, 1));
  const outW = Math.max(1, Math.round(cw * scale));
  const outH = Math.max(1, Math.round(ch * scale));
  const radius = resolveCornerRadiusPx(settings, cw, ch) * scale;

  const fillColor = settings.fillColor ?? "transparent";
  const useFill = !isTransparentFill(fillColor);

  const canvas = document.createElement("canvas");
  canvas.width = outW;
  canvas.height = outH;
  const ctx = canvas.getContext("2d", { alpha: true });
  if (!ctx) {
    throw new Error("Could not create export canvas");
  }

  const imgX = layout.imageX * scale;
  const imgY = layout.imageY * scale;
  const imgW = layout.imageWidth * scale;
  const imgH = layout.imageHeight * scale;
  const imageRadii = clampCornerRadii(
    imgW,
    imgH,
    layout.imageCornerRadii,
    scale
  );

  ctx.clearRect(0, 0, outW, outH);

  if (useFill) {
    ctx.fillStyle = fillColor;
    traceRoundRect(ctx, 0, 0, outW, outH, radius);
    ctx.fill();
  }

  drawRoundedImage(ctx, img, imgX, imgY, imgW, imgH, imageRadii);

  // Outer canvas rounding (padding + transparent fill) — keep corners alpha=0.
  if (radius > 0) {
    applyAlphaMask(ctx, outW, outH, () => {
      traceRoundRect(ctx, 0, 0, outW, outH, radius);
    });
  }

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error("Could not export padded image"));
      },
      "image/png",
      1
    );
  });
}
